import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import useStore from 'zustand/store';

// Create an Axios instance with base configuration
const AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Function to set the Authorization token in request headers
export function setToken(config: InternalAxiosRequestConfig, idToken = '') {
    if (!idToken) return config;
    config.headers.Authorization = `Bearer ${idToken}`;
    return config;
}

// Request interceptor to include the token from local storage
AxiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('sessionId');
        if (token) {
            setToken(config, token);
        }
        return config;
    },
    (error: AxiosError) => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for handling responses and errors
AxiosInstance.interceptors.response.use(
    async (response: AxiosResponse | any) => {
        // Check for 401 in response data (e.g., response.data.code === 401)
        if (response.data.code === 401) {
            const originalRequest = response.config as InternalAxiosRequestConfig;
            try {
                // Call the refresh token endpoint
                const mainssid = localStorage.getItem('sessionId2');
                const ssidData: { ssid1: string, ssid2: string } = JSON.parse(mainssid || '{}');
                const { data } = await ApiNoAuth.post('/auth/refresh-token', {
                    expiredToken: ssidData.ssid1,
                    refreshToken: ssidData.ssid2,
                });

                if (data.data.accessToken) {
                    // Update the access token and refresh token
                    const { accessToken, refreshToken } = data.data;
                    setRefreshAndExpiredToken({ accessToken, refreshToken });

                    setAccessToken(accessToken)
                    setRefreshAndExpiredToken({
                        accessToken: accessToken,
                        refreshToken: refreshToken
                    })
                    // Update the original request with the new access token
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;

                    // Retry the original request
                    return AxiosInstance(originalRequest);
                }
            } catch (refreshError) {
                // If refresh token request fails, open the modal and do not return a response
                useStore.getState().setRefreshTokenModalOpen(true);
                // return Promise.reject(new Error('Token refresh failed'));
                return;
            }
        }
        // Handle successful responses
        return response;
    },
    async (error: AxiosError) => {
        if (error.response?.status === 401) {
            const originalRequest = error.config as InternalAxiosRequestConfig;

            try {
                // Call the refresh token endpoint
                const mainssid = localStorage.getItem('sessionId2');
                const ssidData: { ssid1: string, ssid2: string } = JSON.parse(mainssid || '{}');
                const { data } = await ApiNoAuth.post('/auth/refresh-token', {
                    expiredToken: ssidData.ssid1,
                    refreshToken: ssidData.ssid2,
                });

                if (data.data.accessToken) {
                    // Update the access token and refresh token
                    const { accessToken, refreshToken } = data.data;
                    setRefreshAndExpiredToken({ accessToken, refreshToken });

                    setAccessToken(accessToken)
                    setRefreshAndExpiredToken({
                        accessToken: accessToken,
                        refreshToken: refreshToken
                    })
                    // Update the original request with the new access token
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;

                    // Retry the original request
                    return AxiosInstance(originalRequest);
                }
            } catch (refreshError) {
                // If refresh token request fails, open the modal and do not return a response
                useStore.getState().setRefreshTokenModalOpen(true);
                return;
            }
        }
        // For other errors, reject the promise
        return Promise.reject(error);
    }
);

// A separate Axios instance for requests without authentication
export const ApiNoAuth = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Utility function to store the access token in local storage
export const setAccessToken = (token: string) => {
    localStorage.setItem('sessionId', token);
};

export const setRefreshAndExpiredToken = ({ accessToken, refreshToken }: { accessToken: string, refreshToken: string }) => {
    localStorage.setItem('sessionId2', JSON.stringify({
        ssid1: accessToken,
        ssid2: refreshToken,
    }));
};

export const setSid = (sid: string) => {
    localStorage.setItem('sid', sid);
};

export default AxiosInstance;